import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';

import { ApiError, apiRequest } from '../lib/api-client';

type Status =
  'NEW' | 'PROCESSING' | 'PRODUCTION_COMPLETED' | 'READY_FOR_PICKUP' | 'COMPLETED' | 'CANCELLED';
type StatusFilter = 'ALL' | Status;
type PaymentMethod = 'CASH' | 'QRIS' | 'TRANSFER';
type WorkSource = 'PUSAT' | 'AJISAI_FACTORY' | 'HUB';

type Customer = { id: string; code: string; name: string; phone: string };
type CustomOrder = {
  id: string;
  number: string;
  category: string;
  description: string;
  requiredAt: string;
  workSource: WorkSource;
  sourceCost: number;
  sellingPrice: number;
  grossProfit: number;
  paidAmount: number;
  remainingPayment: number;
  paymentMethod: PaymentMethod;
  notes: string | null;
  status: Status;
  completedAt: string | null;
  cancelledAt: string | null;
  customer: Customer;
  outlet: { id: string; code: string; name: string };
};
type CustomOrderDetail = CustomOrder & {
  orderDate: string;
  createdBy: { id: string; name: string };
  payments: Array<{
    id: string;
    type: 'DOWN_PAYMENT' | 'SETTLEMENT';
    method: PaymentMethod;
    amount: number;
    notes: string | null;
    createdAt: string;
    createdBy: { id: string; name: string };
  }>;
  activities: Array<{ id: string; action: string; createdAt: string; actor: string }>;
};
type CustomOrderMutationResponse = Omit<CustomOrderDetail, 'payments' | 'activities'> & {
  payments?: CustomOrderDetail['payments'];
  activities?: CustomOrderDetail['activities'];
};

const PAGE_SIZE = 10;
const money = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  maximumFractionDigits: 0,
});
const dateTime = new Intl.DateTimeFormat('id-ID', { dateStyle: 'medium', timeStyle: 'short' });
const inputClass =
  'mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal text-slate-900';
const statusLabel: Record<Status, string> = {
  NEW: 'Pesanan Baru',
  PROCESSING: 'Diproses',
  PRODUCTION_COMPLETED: 'Selesai Produksi',
  READY_FOR_PICKUP: 'Siap Diambil',
  COMPLETED: 'Selesai',
  CANCELLED: 'Dibatalkan',
};
const statusStyle: Record<Status, string> = {
  NEW: 'bg-slate-100 text-slate-700',
  PROCESSING: 'bg-amber-100 text-amber-800',
  PRODUCTION_COMPLETED: 'bg-violet-100 text-violet-800',
  READY_FOR_PICKUP: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-emerald-100 text-emerald-800',
  CANCELLED: 'bg-red-100 text-red-800',
};
const nextStatus: Partial<Record<Status, Status>> = {
  NEW: 'PROCESSING',
  PROCESSING: 'PRODUCTION_COMPLETED',
  PRODUCTION_COMPLETED: 'READY_FOR_PICKUP',
  READY_FOR_PICKUP: 'COMPLETED',
};
const paymentLabel: Record<PaymentMethod, string> = {
  CASH: 'Tunai',
  QRIS: 'QRIS',
  TRANSFER: 'Transfer',
};
const sourceLabel: Record<WorkSource, string> = {
  PUSAT: 'Pusat',
  AJISAI_FACTORY: 'Ajisai Factory',
  HUB: 'Hub',
};

export function CustomOrders({
  canWrite,
  outletReady,
  onCompleted,
}: {
  canWrite: boolean;
  outletReady: boolean;
  onCompleted: () => void;
}) {
  const [orders, setOrders] = useState<CustomOrder[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail] = useState<CustomOrderDetail | null>(null);
  const [showPayment, setShowPayment] = useState(false);
  const [form, setForm] = useState(emptyOrderForm());
  const [payment, setPayment] = useState({
    amount: '',
    method: 'CASH' as PaymentMethod,
    notes: '',
  });

  async function load() {
    if (!outletReady) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        search,
        status,
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const result = await apiRequest<{ data: CustomOrder[]; meta: { total: number } }>(
        `/custom-orders?${params}`,
      );
      setOrders(result.data);
      setTotal(result.meta.total);
      setError('');
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Pesanan custom gagal dimuat.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [outletReady, page, status]);

  useEffect(() => {
    if (!outletReady) return;
    apiRequest<{ data: Customer[] }>('/customers?status=ACTIVE&page=1&limit=100')
      .then((result) => setCustomers(result.data))
      .catch(() => setCustomers([]));
  }, [outletReady]);

  async function showDetail(id: string) {
    setError('');
    try {
      setDetail(await apiRequest<CustomOrderDetail>(`/custom-orders/${id}`));
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Detail pesanan gagal dimuat.');
    }
  }

  async function create(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const created = await apiRequest<CustomOrderDetail>('/custom-orders', {
        method: 'POST',
        body: JSON.stringify({
          customerId: form.customerId,
          category: form.category.trim(),
          description: form.description.trim(),
          requiredAt: new Date(form.requiredAt).toISOString(),
          workSource: form.workSource,
          sourceCost: Number(form.sourceCost),
          sellingPrice: Number(form.sellingPrice),
          downPayment: Number(form.downPayment || 0),
          paymentMethod: form.paymentMethod,
          notes: form.notes.trim(),
        }),
      });
      setShowCreate(false);
      setForm(emptyOrderForm());
      setMessage(`Pesanan ${created.number} berhasil dibuat.`);
      setPage(1);
      await load();
      await showDetail(created.id);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Pesanan gagal dibuat.');
    } finally {
      setBusy(false);
    }
  }

  async function addPayment(event: FormEvent) {
    event.preventDefault();
    if (!detail) return;
    setBusy(true);
    setError('');
    try {
      const updated = await apiRequest<CustomOrderMutationResponse>(
        `/custom-orders/${detail.id}/payments`,
        {
          method: 'POST',
          body: JSON.stringify({
            requestId: crypto.randomUUID(),
            amount: Number(payment.amount),
            method: payment.method,
            notes: payment.notes.trim(),
          }),
        },
      );
      setDetail(normalizeDetailResponse(updated, detail));
      setShowPayment(false);
      setPayment({ amount: '', method: 'CASH', notes: '' });
      setMessage('Pembayaran berhasil dicatat.');
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Pembayaran gagal dicatat.');
    } finally {
      setBusy(false);
    }
  }

  async function changeStatus(target: Status) {
    if (!detail) return;
    if (
      target === 'CANCELLED' &&
      !window.confirm(
        detail.paidAmount > 0
          ? 'Pesanan sudah memiliki pembayaran. Batalkan tanpa menghapus riwayat pembayaran?'
          : 'Batalkan pesanan ini? Data pesanan tetap disimpan.',
      )
    )
      return;
    setBusy(true);
    setError('');
    try {
      const updated = await apiRequest<CustomOrderMutationResponse>(
        `/custom-orders/${detail.id}/status`,
        {
          method: 'POST',
          body: JSON.stringify({ status: target }),
        },
      );
      setDetail(normalizeDetailResponse(updated, detail));
      setMessage(`Status diubah menjadi ${statusLabel[target]}.`);
      await load();
      if (target === 'COMPLETED') onCompleted();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Status gagal diperbarui.');
    } finally {
      setBusy(false);
    }
  }

  if (!outletReady) {
    return (
      <div className="rounded-2xl bg-amber-50 p-6 text-amber-800">
        Belum ada outlet aktif. Pilih outlet sebelum membuka Pesanan Custom.
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section className="page-shell">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="font-bold text-ajisai-700">Operasional Outlet</p>
          <h1 className="mt-1 text-3xl font-black tracking-tight">Pesanan Custom</h1>
          <p className="mt-2 text-sm text-slate-500">
            Catat pesanan, pembayaran, dan progres pengerjaan tanpa mengubah stok ready-stock.
          </p>
        </div>
        {canWrite && (
          <button
            className="rounded-xl bg-ajisai-700 px-4 py-3 text-sm font-bold text-white"
            onClick={() => {
              setForm(emptyOrderForm());
              setShowCreate(true);
            }}
          >
            + Pesanan Custom
          </button>
        )}
      </div>

      {error && <p className="mt-5 rounded-xl bg-red-50 p-4 text-sm text-red-700">{error}</p>}
      {message && (
        <p className="mt-5 rounded-xl bg-emerald-50 p-4 text-sm text-emerald-700">{message}</p>
      )}

      <div className="mt-7 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <form
          className="grid gap-3 border-b border-slate-200 p-4 lg:grid-cols-[minmax(220px,1fr)_180px_150px_150px_auto]"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            void load();
          }}
        >
          <input
            aria-label="Cari pesanan custom"
            className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm"
            placeholder="Nomor, customer, telepon, atau jenis"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <select
            className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-bold"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as StatusFilter);
              setPage(1);
            }}
          >
            <option value="ALL">Semua status</option>
            {Object.entries(statusLabel).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <input
            aria-label="Tanggal kebutuhan mulai"
            type="date"
            className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
          <input
            aria-label="Tanggal kebutuhan akhir"
            type="date"
            className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm"
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
          <button className="rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-bold">
            Terapkan
          </button>
        </form>

        <div className="hidden overflow-x-auto lg:block">
          <table className="w-full min-w-[1000px] text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-5 py-3">Pesanan</th>
                <th className="px-5 py-3">Customer</th>
                <th className="px-5 py-3">Dibutuhkan</th>
                <th className="px-5 py-3">Pembayaran</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {orders.map((order) => (
                <tr key={order.id}>
                  <td className="px-5 py-4">
                    <p className="font-bold text-ajisai-800">{order.number}</p>
                    <p className="mt-1 text-xs text-slate-500">{order.category}</p>
                  </td>
                  <td className="px-5 py-4">
                    <p className="font-bold">{order.customer.name}</p>
                    <p className="mt-1 text-xs text-slate-500">{order.customer.phone}</p>
                  </td>
                  <td className="whitespace-nowrap px-5 py-4">
                    {dateTime.format(new Date(order.requiredAt))}
                  </td>
                  <td className="px-5 py-4">
                    <p className="font-bold">{money.format(order.paidAmount)}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      Sisa {money.format(order.remainingPayment)}
                    </p>
                  </td>
                  <td className="px-5 py-4">
                    <StatusBadge status={order.status} />
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button
                      className="font-bold text-ajisai-700"
                      onClick={() => void showDetail(order.id)}
                    >
                      Detail
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="divide-y divide-slate-100 lg:hidden">
          {orders.map((order) => (
            <article key={order.id} className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-black text-ajisai-800">{order.number}</p>
                  <p className="mt-1 text-sm font-medium">{order.category}</p>
                </div>
                <StatusBadge status={order.status} />
              </div>
              <p className="mt-4 font-bold">{order.customer.name}</p>
              <p className="mt-1 text-xs text-slate-500">
                Dibutuhkan {dateTime.format(new Date(order.requiredAt))}
              </p>
              <div className="mt-4 flex items-end justify-between gap-4">
                <div className="text-sm">
                  <p>Dibayar {money.format(order.paidAmount)}</p>
                  <p className="text-slate-500">Sisa {money.format(order.remainingPayment)}</p>
                </div>
                <button
                  className="font-bold text-ajisai-700"
                  onClick={() => void showDetail(order.id)}
                >
                  Detail
                </button>
              </div>
            </article>
          ))}
        </div>

        {loading && <p className="p-12 text-center text-sm text-slate-500">Memuat pesanan...</p>}
        {!loading && orders.length === 0 && (
          <p className="p-12 text-center text-sm text-slate-500">Belum ada pesanan custom.</p>
        )}
        <div className="flex items-center justify-between border-t px-5 py-4 text-sm">
          <span className="text-slate-500">
            {total} pesanan · Halaman {page} dari {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              className="rounded-lg border px-3 py-2 disabled:opacity-40"
              disabled={page === 1}
              onClick={() => setPage((value) => value - 1)}
            >
              Sebelumnya
            </button>
            <button
              className="rounded-lg border px-3 py-2 disabled:opacity-40"
              disabled={page >= totalPages}
              onClick={() => setPage((value) => value + 1)}
            >
              Berikutnya
            </button>
          </div>
        </div>
      </div>

      {showCreate && (
        <OrderFormDialog
          form={form}
          setForm={setForm}
          customers={customers}
          busy={busy}
          onClose={() => setShowCreate(false)}
          onSubmit={(event) => void create(event)}
        />
      )}
      {detail && (
        <OrderDetailDialog
          detail={detail}
          canWrite={canWrite}
          busy={busy}
          onClose={() => setDetail(null)}
          onAddPayment={() => {
            setPayment({
              amount: String(detail.remainingPayment),
              method: detail.paymentMethod,
              notes: '',
            });
            setShowPayment(true);
          }}
          onStatus={(target) => void changeStatus(target)}
        />
      )}
      {showPayment && detail && (
        <PaymentDialog
          payment={payment}
          setPayment={setPayment}
          remaining={detail.remainingPayment}
          busy={busy}
          onClose={() => setShowPayment(false)}
          onSubmit={(event) => void addPayment(event)}
        />
      )}
    </section>
  );
}

function emptyOrderForm() {
  const tomorrow = new Date(Date.now() + 86_400_000);
  const local = new Date(tomorrow.getTime() - tomorrow.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 16);
  return {
    customerId: '',
    category: '',
    description: '',
    requiredAt: local,
    workSource: 'PUSAT' as WorkSource,
    sourceCost: '',
    sellingPrice: '',
    downPayment: '',
    paymentMethod: 'CASH' as PaymentMethod,
    notes: '',
  };
}

function normalizeDetailResponse(
  response: CustomOrderMutationResponse,
  fallback: CustomOrderDetail,
): CustomOrderDetail {
  return {
    ...response,
    payments: Array.isArray(response.payments) ? response.payments : fallback.payments,
    activities: Array.isArray(response.activities) ? response.activities : fallback.activities,
  };
}

type OrderForm = ReturnType<typeof emptyOrderForm>;

function OrderFormDialog({
  form,
  setForm,
  customers,
  busy,
  onClose,
  onSubmit,
}: {
  form: OrderForm;
  setForm: (value: OrderForm) => void;
  customers: Customer[];
  busy: boolean;
  onClose: () => void;
  onSubmit: (event: FormEvent) => void;
}) {
  const profit = Number(form.sellingPrice || 0) - Number(form.sourceCost || 0);
  const remaining = Number(form.sellingPrice || 0) - Number(form.downPayment || 0);
  return (
    <Dialog title="Tambah Pesanan Custom" onClose={onClose} size="max-w-3xl">
      <form className="grid gap-5 sm:grid-cols-2" onSubmit={onSubmit}>
        <Field label="Customer">
          <select
            required
            className={inputClass}
            value={form.customerId}
            onChange={(event) => setForm({ ...form, customerId: event.target.value })}
          >
            <option value="">Pilih customer aktif</option>
            {customers.map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.name} · {customer.phone}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Jenis/Kategori Pesanan">
          <input
            required
            maxLength={120}
            className={inputClass}
            placeholder="Contoh: Buket Custom"
            value={form.category}
            onChange={(event) => setForm({ ...form, category: event.target.value })}
          />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Deskripsi Custom">
            <textarea
              required
              maxLength={2000}
              className={`${inputClass} min-h-28`}
              placeholder="Warna, tema, ukuran, ucapan, dan permintaan penting"
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />
          </Field>
        </div>
        <Field label="Tanggal/Jam Dibutuhkan">
          <input
            required
            type="datetime-local"
            className={inputClass}
            value={form.requiredAt}
            onChange={(event) => setForm({ ...form, requiredAt: event.target.value })}
          />
        </Field>
        <Field label="Dikerjakan oleh">
          <select
            className={inputClass}
            value={form.workSource}
            onChange={(event) => setForm({ ...form, workSource: event.target.value as WorkSource })}
          >
            {Object.entries(sourceLabel).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Harga dari Pusat/Factory/Hub">
          <input
            required
            min="0"
            type="number"
            className={inputClass}
            value={form.sourceCost}
            onChange={(event) => setForm({ ...form, sourceCost: event.target.value })}
          />
        </Field>
        <Field label="Harga Jual">
          <input
            required
            min="0"
            type="number"
            className={inputClass}
            value={form.sellingPrice}
            onChange={(event) => setForm({ ...form, sellingPrice: event.target.value })}
          />
        </Field>
        <Field label="DP Diterima">
          <input
            min="0"
            max={Number(form.sellingPrice || 0)}
            type="number"
            className={inputClass}
            value={form.downPayment}
            onChange={(event) => setForm({ ...form, downPayment: event.target.value })}
          />
        </Field>
        <Field label="Metode Pembayaran">
          <select
            className={inputClass}
            value={form.paymentMethod}
            onChange={(event) =>
              setForm({ ...form, paymentMethod: event.target.value as PaymentMethod })
            }
          >
            {Object.entries(paymentLabel).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <div className="sm:col-span-2 grid gap-3 rounded-xl bg-slate-50 p-4 sm:grid-cols-2">
          <Summary label="Laba Kotor" value={money.format(profit)} />
          <Summary label="Sisa Pembayaran" value={money.format(Math.max(0, remaining))} />
        </div>
        <div className="sm:col-span-2">
          <Field label="Catatan (opsional)">
            <textarea
              maxLength={1000}
              className={`${inputClass} min-h-20`}
              value={form.notes}
              onChange={(event) => setForm({ ...form, notes: event.target.value })}
            />
          </Field>
        </div>
        <DialogActions busy={busy} onClose={onClose} submitLabel="Simpan Pesanan" />
      </form>
    </Dialog>
  );
}

function OrderDetailDialog({
  detail,
  canWrite,
  busy,
  onClose,
  onAddPayment,
  onStatus,
}: {
  detail: CustomOrderDetail;
  canWrite: boolean;
  busy: boolean;
  onClose: () => void;
  onAddPayment: () => void;
  onStatus: (status: Status) => void;
}) {
  const next = nextStatus[detail.status];
  const final = detail.status === 'COMPLETED' || detail.status === 'CANCELLED';
  const payments = Array.isArray(detail.payments) ? detail.payments : [];
  const activities = Array.isArray(detail.activities) ? detail.activities : [];
  return (
    <Dialog title={detail.number} onClose={onClose} size="max-w-5xl">
      <div className="flex flex-wrap items-center gap-3">
        <StatusBadge status={detail.status} />
        <span className="text-sm text-slate-500">{detail.outlet.name}</span>
        <span className="text-sm text-slate-500">· {sourceLabel[detail.workSource]}</span>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard label="Harga Jual" value={money.format(detail.sellingPrice)} />
        <SummaryCard label="Laba Kotor" value={money.format(detail.grossProfit)} />
        <SummaryCard label="Sudah Dibayar" value={money.format(detail.paidAmount)} />
        <SummaryCard label="Sisa Pembayaran" value={money.format(detail.remainingPayment)} />
      </div>
      <div className="mt-6 grid gap-4 rounded-xl bg-slate-50 p-5 text-sm sm:grid-cols-2">
        <Info label="Customer" value={`${detail.customer.name} · ${detail.customer.phone}`} />
        <Info label="Dibutuhkan" value={dateTime.format(new Date(detail.requiredAt))} />
        <Info label="Jenis" value={detail.category} />
        <Info label="Harga Pusat/Factory/Hub" value={money.format(detail.sourceCost)} />
        <div className="sm:col-span-2">
          <Info label="Deskripsi Custom" value={detail.description} />
        </div>
        <div className="sm:col-span-2">
          <Info label="Catatan" value={detail.notes || '—'} />
        </div>
      </div>

      {canWrite && !final && (
        <div className="mt-6 flex flex-wrap gap-3 rounded-xl border border-slate-200 p-4">
          {detail.remainingPayment > 0 && (
            <button
              disabled={busy}
              className="rounded-xl bg-ajisai-700 px-4 py-2.5 text-sm font-bold text-white"
              onClick={onAddPayment}
            >
              + Catat Pembayaran
            </button>
          )}
          {next && (
            <button
              disabled={busy || (next === 'COMPLETED' && detail.remainingPayment > 0)}
              className="rounded-xl border border-ajisai-300 px-4 py-2.5 text-sm font-bold text-ajisai-800 disabled:opacity-40"
              onClick={() => onStatus(next)}
            >
              Lanjut ke {statusLabel[next]}
            </button>
          )}
          <button
            disabled={busy}
            className="rounded-xl border border-red-200 px-4 py-2.5 text-sm font-bold text-red-700"
            onClick={() => onStatus('CANCELLED')}
          >
            Batalkan Pesanan
          </button>
        </div>
      )}

      <div className="mt-7 grid gap-6 lg:grid-cols-2">
        <HistorySection title="Riwayat Pembayaran">
          {payments.map((item) => (
            <div key={item.id} className="border-b border-slate-100 p-4 last:border-0">
              <div className="flex justify-between gap-4">
                <div>
                  <p className="font-bold">
                    {item.type === 'SETTLEMENT' ? 'Pelunasan' : 'DP'} · {paymentLabel[item.method]}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {dateTime.format(new Date(item.createdAt))} · {item.createdBy.name}
                  </p>
                </div>
                <p className="font-black">{money.format(item.amount)}</p>
              </div>
            </div>
          ))}
          {payments.length === 0 && <EmptyHistory text="Belum ada pembayaran." />}
        </HistorySection>
        <HistorySection title="Audit Aktivitas">
          {activities.map((item) => (
            <div key={item.id} className="border-b border-slate-100 p-4 last:border-0">
              <p className="font-bold">{activityLabel(item.action)}</p>
              <p className="mt-1 text-xs text-slate-500">
                {dateTime.format(new Date(item.createdAt))} · {item.actor}
              </p>
            </div>
          ))}
          {activities.length === 0 && <EmptyHistory text="Belum ada aktivitas." />}
        </HistorySection>
      </div>
    </Dialog>
  );
}

function PaymentDialog({
  payment,
  setPayment,
  remaining,
  busy,
  onClose,
  onSubmit,
}: {
  payment: { amount: string; method: PaymentMethod; notes: string };
  setPayment: (value: { amount: string; method: PaymentMethod; notes: string }) => void;
  remaining: number;
  busy: boolean;
  onClose: () => void;
  onSubmit: (event: FormEvent) => void;
}) {
  return (
    <Dialog title="Catat Pembayaran" onClose={onClose} size="max-w-md">
      <p className="mb-5 rounded-xl bg-amber-50 p-3 text-sm text-amber-800">
        Sisa tagihan: <strong>{money.format(remaining)}</strong>
      </p>
      <form className="grid gap-5" onSubmit={onSubmit}>
        <Field label="Nominal">
          <input
            required
            type="number"
            min="1"
            max={remaining}
            className={inputClass}
            value={payment.amount}
            onChange={(event) => setPayment({ ...payment, amount: event.target.value })}
          />
        </Field>
        <Field label="Metode Pembayaran">
          <select
            className={inputClass}
            value={payment.method}
            onChange={(event) =>
              setPayment({ ...payment, method: event.target.value as PaymentMethod })
            }
          >
            {Object.entries(paymentLabel).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Catatan (opsional)">
          <textarea
            maxLength={500}
            className={`${inputClass} min-h-20`}
            value={payment.notes}
            onChange={(event) => setPayment({ ...payment, notes: event.target.value })}
          />
        </Field>
        <DialogActions busy={busy} onClose={onClose} submitLabel="Simpan Pembayaran" />
      </form>
    </Dialog>
  );
}

function Dialog({
  title,
  onClose,
  size,
  children,
}: {
  title: string;
  onClose: () => void;
  size: string;
  children: ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/50 p-4" role="dialog">
      <div
        className={`max-h-[92vh] w-full ${size} overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl`}
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <h2 className="text-2xl font-black">{title}</h2>
          <button aria-label="Tutup" className="text-xl text-slate-500" onClick={onClose}>
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="text-sm font-bold text-slate-700">
      {label}
      {children}
    </label>
  );
}

function DialogActions({
  busy,
  onClose,
  submitLabel,
}: {
  busy: boolean;
  onClose: () => void;
  submitLabel: string;
}) {
  return (
    <div className="flex justify-end gap-3 sm:col-span-2">
      <button type="button" className="rounded-xl border px-4 py-3 font-bold" onClick={onClose}>
        Batal
      </button>
      <button
        disabled={busy}
        className="rounded-xl bg-ajisai-700 px-5 py-3 font-bold text-white disabled:opacity-50"
      >
        {busy ? 'Menyimpan...' : submitLabel}
      </button>
    </div>
  );
}

function StatusBadge({ status }: { status: Status }) {
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${statusStyle[status]}`}
    >
      {statusLabel[status]}
    </span>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="mt-1 font-black">{value}</p>
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="mt-2 break-words font-black">{value}</p>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-slate-500">{label}</p>
      <p className="mt-1 whitespace-pre-wrap font-bold">{value}</p>
    </div>
  );
}

function HistorySection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="overflow-hidden rounded-xl border border-slate-200">
      <h3 className="border-b bg-slate-50 p-4 font-black">{title}</h3>
      {children}
    </section>
  );
}

function EmptyHistory({ text }: { text: string }) {
  return <p className="p-8 text-center text-sm text-slate-500">{text}</p>;
}

function activityLabel(action: string) {
  return (
    {
      CREATE: 'Pesanan dibuat',
      PAYMENT_ADDED: 'Pembayaran ditambahkan',
      STATUS_CHANGED: 'Status diperbarui',
      CANCELLED: 'Pesanan dibatalkan',
    }[action] ?? action
  );
}
